import React from 'react';
import PropTypes from 'prop-types';
import { createMemoStyles, defaultFonts, safeRest } from '@wds/utils';
import { useTheme } from 'react-jss';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var useStyles = createMemoStyles(function (theme) {
  var spacing = theme.spacing,
      border = theme.border,
      radius = theme.radius,
      shadow = theme.shadow,
      palette = theme.palette;
  var card = {
    borderRadius: radius.small,
    boxShadow: shadow.none,
    border: border.muted,
    padding: spacing[5],
    bgColor: palette.background.light
  };
  return {
    wrenchCard: function wrenchCard(_ref) {
      var selected = _ref.selected,
          interactive = _ref.interactive;
      return _objectSpread(_objectSpread(_objectSpread({}, defaultFonts(theme)), {}, {
        backgroundColor: card.bgColor,
        border: card.border,
        borderRadius: card.borderRadius,
        boxShadow: card.boxShadow,
        // property will get overwritten, but needed the `cursor: pointer`
        // when interactive
        borderColor: card.borderColor
      }, interactive && {
        '&:hover, &:focus': {
          borderColor: palette.border.hover,
          cursor: 'pointer'
        }
      }), selected && {
        borderColor: palette.border.active,
        '&:hover, &focus': {
          borderColor: palette.border.active
        }
      });
    },
    wrenchCardHeader: function wrenchCardHeader(_ref2) {
      var noRule = _ref2.noRule;
      return _objectSpread(_objectSpread({}, noRule ? {
        borderBottom: 'none'
      } : {
        borderBottom: border.muted
      }), {}, {
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: '0',
        padding: [spacing[4], spacing[5]],
        minHeight: '72px'
      });
    },
    wrenchCardBody: function wrenchCardBody() {
      return {
        padding: card.padding
      };
    },
    wrenchCardAddon: function wrenchCardAddon() {
      return {
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        minHeight: '40px',
        margin: [0, 0, 'auto', spacing[4]],
        float: 'right'
      };
    }
  };
});

var _excluded = ["children", "selected", "title", "padding", "addOn", "interactive", "noRule"];

var Card = function Card(_ref) {
  var children = _ref.children,
      selected = _ref.selected,
      title = _ref.title,
      padding = _ref.padding,
      addOn = _ref.addOn,
      interactive = _ref.interactive,
      noRule = _ref.noRule,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();

  var _useStyles = useStyles({
    selected: selected,
    theme: theme,
    noRule: noRule,
    interactive: interactive
  }),
      wrenchCard = _useStyles.wrenchCard,
      wrenchCardBody = _useStyles.wrenchCardBody,
      wrenchCardHeader = _useStyles.wrenchCardHeader,
      wrenchCardAddon = _useStyles.wrenchCardAddon;

  var cardAddon = /*#__PURE__*/React.createElement("div", {
    className: wrenchCardAddon
  }, addOn);
  var hadAddOn = !title && addOn && cardAddon;
  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: wrenchCard
  }, safeRest(rest)), title ? /*#__PURE__*/React.createElement("header", {
    className: wrenchCardHeader
  }, title, addOn && cardAddon) : null, padding ? /*#__PURE__*/React.createElement("div", {
    className: wrenchCardBody
  }, hadAddOn, children) : /*#__PURE__*/React.createElement(React.Fragment, null, hadAddOn, children));
};

Card.displayName = "Card";
Card.propTypes = {
  /**
   * Custom right side addon, can be text or components.
   */
  addOn: PropTypes.node,
  children: PropTypes.node.isRequired,

  /**
   * Enable hover styles.
   */
  interactive: PropTypes.bool,

  /**
   * 'Set visibility of divider line.',
   */
  noRule: PropTypes.bool,

  /**
   * Card has built in padding for it&apo;s content but can be turned off.
   */
  padding: PropTypes.bool,

  /**
   * Enable selected card outline.
   */
  selected: PropTypes.bool,

  /**
   * Top section of Card with text title,
   */
  title: PropTypes.node
};
Card.defaultProps = {
  addOn: undefined,
  interactive: false,
  noRule: false,
  padding: true,
  selected: false,
  title: undefined
};
Card.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Card",
  "props": {
    "addOn": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Custom right side addon, can be text or components."
    },
    "interactive": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Enable hover styles."
    },
    "noRule": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "'Set visibility of divider line.',"
    },
    "padding": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Card has built in padding for it&apo;s content but can be turned off."
    },
    "selected": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Enable selected card outline."
    },
    "title": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Top section of Card with text title,"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": ""
    }
  }
};

export { Card };
